import { AppBadge, AppCombobox } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { MemberCombobox } from '@inquiries/-components/MemberCombobox'
import { useDivisionOptions } from '@inquiries/-hooks/useDivisionOptions'
import { useInquiryRoleMembers } from '@inquiries/-hooks/useInquiryRoleMembers'
import type { InquiryResponse } from 'backend/types'
import { useMemo } from 'react'
import { PKS_MESSAGES } from 'shared'
import type { InquiryEditFormValues } from '../../../hooks/useInquiryEditForm'
import { useInquiryRelatedDivisions } from '../../../hooks/useInquiryRelatedDivisions'

export type AddedRelatedDivision = { divisionId: number; name: string }

const toMemberOption = (m: { id: number; name: string; employeeNumber: number | null }) => ({
  value: String(m.id),
  label: m.name,
  data: { employeeNumber: m.employeeNumber },
})

type StaffSectionProps = {
  inquiry: InquiryResponse
  formValues: InquiryEditFormValues
  onFieldChange: <K extends keyof InquiryEditFormValues>(key: K, value: InquiryEditFormValues[K]) => void
  addedRelatedDivisions: AddedRelatedDivision[]
  removedRelatedDivisionIds: number[]
  onAddRelatedDivision: (divisionId: number, name: string) => void
  onRemoveExistingRelatedDivision: (id: number) => void
  onRemoveAddedRelatedDivision: (divisionId: number) => void
  /** 主管部門が SF プロジェクト側で決まっていて、引合側では編集できない */
  isPrimaryDivisionLocked: boolean
  /** 主管部門の欄を非活性にする（SF 側で決まる、または引合が確定済み） */
  isPrimaryDivisionDisabled: boolean
  isInquiryCompleted: boolean
}

export function StaffSection({
  inquiry,
  formValues,
  onFieldChange,
  addedRelatedDivisions,
  removedRelatedDivisionIds,
  onAddRelatedDivision,
  onRemoveExistingRelatedDivision,
  onRemoveAddedRelatedDivision,
  isPrimaryDivisionLocked,
  isPrimaryDivisionDisabled,
  isInquiryCompleted,
}: StaffSectionProps) {
  const hasSalesforceProject = inquiry.salesforce_project_id != null || formValues.salesforceProjectId != null

  const { divisionOptions: baseDivisionOptions } = useDivisionOptions({ isEnabled: true, isAssignable: true })
  const divisionOptions = useMemo(() => {
    if (
      inquiry.primary_division_id != null &&
      inquiry.short_primary_division_name &&
      !baseDivisionOptions.some((o) => o.value === String(inquiry.primary_division_id))
    ) {
      return [
        { value: String(inquiry.primary_division_id), label: inquiry.short_primary_division_name },
        ...baseDivisionOptions,
      ]
    }
    return baseDivisionOptions
  }, [baseDivisionOptions, inquiry.primary_division_id, inquiry.short_primary_division_name])
  // ANO を替えて入った SF 側の部門は割当可能な部門に無いことがあるため、全部門から名前を引いて主管部門の欄にだけ補う
  const { divisionOptions: allDivisionOptions } = useDivisionOptions()
  const primaryDivisionOptions = useMemo(() => {
    const primaryDivisionId = formValues.primaryDivisionId
    if (primaryDivisionId == null || divisionOptions.some((o) => o.value === String(primaryDivisionId))) {
      return divisionOptions
    }
    const option = allDivisionOptions.find((o) => o.value === String(primaryDivisionId))
    return option ? [option, ...divisionOptions] : divisionOptions
  }, [divisionOptions, allDivisionOptions, formValues.primaryDivisionId])
  const { data: relatedDivisionsData } = useInquiryRelatedDivisions(inquiry.id)
  const relatedDivisions = relatedDivisionsData?.inquiry_related_divisions ?? []
  const { data: roleMembersData } = useInquiryRoleMembers(hasSalesforceProject ? { isManager: true } : {})
  const roleMembers = roleMembersData?.members ?? []

  const salesMemberOptions = useMemo(() => {
    const options = roleMembers.filter((m) => m.role === 'sales').map(toMemberOption)
    if (
      inquiry.sales_member_id != null &&
      inquiry.sales_member_name &&
      !options.some((o) => o.value === String(inquiry.sales_member_id))
    ) {
      options.unshift({
        value: String(inquiry.sales_member_id),
        label: inquiry.sales_member_name,
        data: { employeeNumber: inquiry.sales_member_employee_number },
      })
    }
    return options
  }, [roleMembers, inquiry.sales_member_id, inquiry.sales_member_name, inquiry.sales_member_employee_number])

  const smgMemberOptions = useMemo(() => {
    const options = roleMembers.filter((m) => m.role === 'smg').map(toMemberOption)
    if (
      inquiry.service_manager_member_id != null &&
      inquiry.service_manager_name &&
      !options.some((o) => o.value === String(inquiry.service_manager_member_id))
    ) {
      options.unshift({
        value: String(inquiry.service_manager_member_id),
        label: inquiry.service_manager_name,
        data: { employeeNumber: inquiry.service_manager_employee_number },
      })
    }
    return options
  }, [
    roleMembers,
    inquiry.service_manager_member_id,
    inquiry.service_manager_name,
    inquiry.service_manager_employee_number,
  ])

  const coordinationMemberOptions = useMemo(() => {
    const options = roleMembers.filter((m) => m.role === 'manager').map(toMemberOption)
    if (
      inquiry.coordination_member_id != null &&
      inquiry.coordination_member_name &&
      !options.some((o) => o.value === String(inquiry.coordination_member_id))
    ) {
      options.unshift({
        value: String(inquiry.coordination_member_id),
        label: inquiry.coordination_member_name,
        data: { employeeNumber: null },
      })
    }
    return options
  }, [roleMembers, inquiry.coordination_member_id, inquiry.coordination_member_name])

  const visibleRelatedDivisions = relatedDivisions.filter((d) => !removedRelatedDivisionIds.includes(d.id))

  const relatedDivisionBadges = [
    ...visibleRelatedDivisions.map((d) => ({
      key: String(d.id),
      name: d.short_division_name ?? d.division_name,
      color: 'secondary' as const,
      onRemove: () => onRemoveExistingRelatedDivision(d.id),
    })),
    ...addedRelatedDivisions.map((d) => ({
      key: `added-${d.divisionId}`,
      name: d.name,
      color: 'blue' as const,
      onRemove: () => onRemoveAddedRelatedDivision(d.divisionId),
    })),
  ]

  // 既に追加済み・表示中の部門を除外した選択肢
  const existingDivisionIds = new Set([
    ...visibleRelatedDivisions.map((d) => d.division_id),
    ...addedRelatedDivisions.map((d) => d.divisionId),
  ])
  const relatedDivisionOptions = divisionOptions.filter((o) => !existingDivisionIds.has(Number(o.value)))

  const handleAddDivision = (value: string) => {
    const divisionId = Number(value)
    const option = relatedDivisionOptions.find((o) => o.value === value)
    if (!option) return
    onAddRelatedDivision(divisionId, option.label)
  }

  return (
    <section className='space-y-3'>
      <h3 className='text-base font-semibold'>担当者</h3>
      <div className='space-y-3'>
        <AppCombobox
          value={formValues.primaryDivisionId != null ? String(formValues.primaryDivisionId) : undefined}
          onValueChange={(value) => onFieldChange('primaryDivisionId', value ? Number(value) : null)}
          options={primaryDivisionOptions}
          placeholder='部門を選択'
          clearable
          field={{
            label: '主管部門',
            helpText: isPrimaryDivisionLocked ? PKS_MESSAGES.EDIT_PRIMARY_DIVISION_IN_PKS : undefined,
          }}
          search={{ placeholder: '部門名で検索...', emptyMessage: '部門が見つかりません' }}
          disabled={isPrimaryDivisionDisabled}
        />
        <div className='space-y-2'>
          <span className='text-xs font-medium text-muted-foreground'>関連部門</span>
          <div className='flex flex-wrap items-center gap-2'>
            {relatedDivisionBadges.map((item) => (
              <AppBadge key={item.key} color={item.color} onDismiss={isInquiryCompleted ? undefined : item.onRemove}>
                {item.name}
              </AppBadge>
            ))}
          </div>
          {relatedDivisionOptions.length > 0 && (
            <AppCombobox
              value=''
              onValueChange={handleAddDivision}
              options={relatedDivisionOptions}
              placeholder='+ 追加'
              className='w-32'
              customization={{ size: 'sm' }}
              search={{ placeholder: '部門名で検索...', emptyMessage: '部門が見つかりません' }}
              disabled={isInquiryCompleted}
            />
          )}
        </div>
        <MemberCombobox
          value={formValues.salesMemberId != null ? String(formValues.salesMemberId) : undefined}
          onValueChange={(value) => onFieldChange('salesMemberId', value ? Number(value) : null)}
          options={salesMemberOptions}
          label='営業担当'
          disabled={hasSalesforceProject || isInquiryCompleted}
        />
        <MemberCombobox
          value={formValues.serviceManagerMemberId != null ? String(formValues.serviceManagerMemberId) : undefined}
          onValueChange={(value) => onFieldChange('serviceManagerMemberId', value ? Number(value) : null)}
          options={smgMemberOptions}
          label='SMG担当'
          disabled={hasSalesforceProject || isInquiryCompleted}
          message={hasSalesforceProject ? PKS_MESSAGES.EDIT_SALES_SMG_IN_PKS : undefined}
        />
        <MemberCombobox
          value={formValues.coordinationMemberId != null ? String(formValues.coordinationMemberId) : undefined}
          onValueChange={(value) => onFieldChange('coordinationMemberId', value ? Number(value) : null)}
          options={coordinationMemberOptions}
          label='調達・調整担当'
          disabled={isInquiryCompleted}
        />
      </div>
    </section>
  )
}
